/**
 * Item availability service (static, offline).
 *
 * HISTORY: this used to call the Divine Pride API (divine-pride.net/api) once
 * PER ITEM, PER PAGE LOAD, for every visitor — ~1,600 requests on every load,
 * none of it persisted across reloads. That volume contributed to Divine Pride
 * having to rate-limit / shut down their API. This module no longer makes ANY
 * network requests.
 *
 * Item availability on LatamRO is effectively static, so it is shipped as a
 * static file (src/db/latamAvailability.json) and looked up synchronously.
 * Regenerate that file offline with scripts/fetch-latam-availability.mjs when
 * API access is available — it runs ONCE, not per visitor.
 *
 * The async signatures below are preserved so existing callers (the item-list
 * components, randomCharacter) keep working unchanged.
 */

import latamAvailability from '@/db/latamAvailability.json';
import itemOverrides from '@/config/itemOverrides';

// Build the available-id Set once at module load.
const AVAILABLE_IDS = new Set(
  (latamAvailability.availableIds || []).map((id) => parseInt(id, 10))
);

// Whether we actually have availability data. When false, callers should treat
// every item as available (i.e. the filter shows everything) rather than hiding
// the whole catalog.
export const hasAvailabilityData = () => AVAILABLE_IDS.size > 0;

/**
 * Synchronously return the set of item IDs available on the given server.
 * Used by the randomize feature to pick from the available pool.
 * @returns {Set<number>}
 */
export const getCachedAvailableIds = (/* server = 'LATAM' */) => {
  return new Set(AVAILABLE_IDS);
};

/**
 * Whether a single item is available on the server.
 * Honors the manual whitelist/blacklist overrides.
 * @returns {boolean}
 */
const isAvailable = (itemId) => {
  const id = parseInt(itemId, 10);
  if (itemOverrides.blacklist.includes(id)) return false;
  if (itemOverrides.whitelist.includes(id)) return true;
  // No data yet → treat everything as available (filter is a no-op).
  if (!hasAvailabilityData()) return true;
  return AVAILABLE_IDS.has(id);
};

/**
 * Async-compatible single check (kept async for signature compatibility).
 * @returns {Promise<boolean>}
 */
export const isItemAvailableOnServer = async (itemId /*, server */) => {
  return isAvailable(itemId);
};

/**
 * Batch availability lookup. No network — resolves immediately from the static
 * data. Returns a Map<numericId, boolean>, matching the previous contract.
 * @returns {Promise<Map<number, boolean>>}
 */
export const batchCheckItemsAvailability = async (itemIds /*, server */) => {
  const results = new Map();
  for (const itemId of itemIds) {
    const id = parseInt(itemId, 10);
    results.set(id, isAvailable(id));
  }
  return results;
};

/**
 * Item names now come from the bundled itemsPt.json / itemsEn.json, so there is
 * no need to fetch them. Kept as a no-op for signature compatibility.
 * @returns {Promise<string>}
 */
export const getItemName = async (itemId) => {
  return `Item #${parseInt(itemId, 10)}`;
};

/**
 * Retained for signature compatibility; no network, always null.
 * @returns {Promise<null>}
 */
export const fetchItemData = async () => null;

/**
 * Pure helper — returns an item's location type if present. No network.
 */
export const getItemLocationType = (item) => {
  if (!item || !item.location) return null;
  return item.location;
};
